import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useT } from '../i18n'
import {
  cacheMatrix,
  cloneMatrix,
  getDefaultMatrixForSpecies,
  isBlankMatrix,
  loadCachedMatrix,
  markLocalMortalityMatrixEdit
} from '../utils/mortalityMatrix'
import { loadMortalityMatrix, retryMortalityMatrixSync, saveMortalityMatrix } from '../utils/mortalityMatrixApi'
import MatrixTable, { type MatrixData } from './MatrixTable'

const SAVE_DEBOUNCE_MS = 600

type MortalityMatrixProps = {
  species?: string
  refreshKey?: number
  onStatusChange?: (status: MortalityMatrixStatusPresentation | null) => void
  onMatrixChange?: (data: MatrixData) => void
  externalMatrix?: MatrixData
  externalMatrixVersion?: number
  persistenceMode?: 'species' | 'external'
}

type SaveStatus = 'loading' | 'saving' | 'refreshing' | 'error' | 'offline' | 'pending' | 'synced'
type StatusTone = 'info' | 'success' | 'error'

export type MortalityMatrixStatusPresentation = {
  state: SaveStatus
  tone: StatusTone
  message: string
}

const statusBannerStyle = (tone: StatusTone): React.CSSProperties => ({
  marginBottom: '16px',
  padding: '12px 16px',
  borderRadius: '10px',
  border: tone === 'success' ? '1px solid #86efac' : tone === 'error' ? '1px solid #fecaca' : '1px solid #bae6fd',
  background: tone === 'success' ? '#f0fdf4' : tone === 'error' ? '#fef2f2' : '#f0f9ff',
  color: tone === 'success' ? '#166534' : tone === 'error' ? '#b91c1c' : '#075985',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '1.5'
})

export const getMortalityMatrixStatusPresentation = (
  status: SaveStatus,
  translate: (key: string) => string,
  errorMessage: string | null
): MortalityMatrixStatusPresentation | null => {
  switch (status) {
    case 'loading':
      return { state: status, tone: 'info', message: translate('mortalityManagement.status.loading') }
    case 'saving':
      return { state: status, tone: 'info', message: translate('mortalityManagement.status.saving') }
    case 'refreshing':
      return { state: status, tone: 'info', message: translate('mortalityManagement.status.refreshing') }
    case 'offline':
      return { state: status, tone: 'info', message: translate('mortalityManagement.status.offline') }
    case 'pending':
      return { state: status, tone: 'info', message: translate('mortalityManagement.status.pending') }
    case 'synced':
      return { state: status, tone: 'success', message: translate('mortalityManagement.status.synced') }
    case 'error':
      return { state: status, tone: 'error', message: errorMessage ?? translate('mortalityManagement.status.refreshFailed') }
    default:
      return null
  }
}

const noticeCardStyle = (tone: 'info' | 'error'): React.CSSProperties => ({
  marginBottom: '16px',
  padding: '14px 16px',
  borderRadius: '10px',
  border: tone === 'error' ? '1px solid #fecaca' : '1px solid #bfdbfe',
  background: tone === 'error' ? '#fef2f2' : '#eff6ff',
  color: tone === 'error' ? '#b91c1c' : '#1e3a8a',
  fontSize: '14px',
  lineHeight: '1.5'
})

const MatrixSkeleton: React.FC<{ label: string }> = ({ label }) => (
  <div
    aria-hidden="true"
    style={{
      border: '1px dashed #cbd5e1',
      borderRadius: '12px',
      padding: '32px 20px',
      background: '#f8fafc',
      color: '#64748b',
      fontSize: '14px',
      textAlign: 'center'
    }}
  >
    {label}
  </div>
)

const MortalityMatrix: React.FC<MortalityMatrixProps> = ({
  species = 'global',
  refreshKey,
  onStatusChange,
  onMatrixChange,
  externalMatrix,
  externalMatrixVersion,
  persistenceMode = 'species'
}) => {
  const t = useT()
  const [matrixData, setMatrixData] = useState<MatrixData>(() => getDefaultMatrixForSpecies(species))
  const [status, setStatus] = useState<SaveStatus>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [dataSource, setDataSource] = useState<'server' | 'local' | null>(null)
  const [retryToken, setRetryToken] = useState(0)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const skipNextSaveRef = useRef(true)
  const refreshKeyRef = useRef(refreshKey)
  const retryTokenRef = useRef(retryToken)
  const statusVersionRef = useRef(0)
  const onMatrixChangeRef = useRef<typeof onMatrixChange>()

  useEffect(() => {
    onMatrixChangeRef.current = onMatrixChange
  }, [onMatrixChange])

  useEffect(() => () => {
    if (saveTimeoutRef.current) {
      globalThis.clearTimeout(saveTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (!externalMatrix) return
    skipNextSaveRef.current = false
    setMatrixData(cloneMatrix(externalMatrix))
  }, [externalMatrixVersion, externalMatrix, species])

  useEffect(() => {
    if (persistenceMode === 'external') {
      statusVersionRef.current += 1
      setStatus('synced')
      setErrorMessage(null)
      setDataSource(null)

      skipNextSaveRef.current = true
      setMatrixData(cloneMatrix(externalMatrix ?? getDefaultMatrixForSpecies(species)))
      return
    }

    skipNextSaveRef.current = true
    const forceRefresh =
      refreshKey !== refreshKeyRef.current || retryToken !== retryTokenRef.current
    refreshKeyRef.current = refreshKey
    retryTokenRef.current = retryToken

    statusVersionRef.current += 1
    setStatus(forceRefresh ? 'refreshing' : 'loading')
    setErrorMessage(null)

    let cancelled = false

    const localMatrix = cloneMatrix(loadCachedMatrix(species) ?? getDefaultMatrixForSpecies(species))
    skipNextSaveRef.current = true
    setMatrixData(localMatrix)
    onMatrixChangeRef.current?.(localMatrix)

    const load = async () => {
      const result = await loadMortalityMatrix(species, { forceRefresh })

      if (cancelled) {
        return
      }

      skipNextSaveRef.current = true
      const cloned = cloneMatrix(result.matrix)
      setMatrixData(cloned)
      onMatrixChangeRef.current?.(cloned)
      setDataSource(result.source)

      if (result.errorMessage) {
        setStatus('error')
        setErrorMessage(
          isBlankMatrix(result.matrix)
            ? t('mortalityManagement.status.refreshFailedBlank')
            : t('mortalityManagement.status.refreshFailed')
        )
        return
      }

      setStatus(result.syncState)
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [externalMatrix, persistenceMode, refreshKey, retryToken, species, t])

  useEffect(() => {
    if (persistenceMode === 'external') {
      return
    }

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false
      return
    }

    if (saveTimeoutRef.current) {
      globalThis.clearTimeout(saveTimeoutRef.current)
    }

    setStatus('saving')
    setErrorMessage(null)

    saveTimeoutRef.current = globalThis.setTimeout(async () => {
      const statusVersion = statusVersionRef.current + 1
      statusVersionRef.current = statusVersion

      try {
        const result = await saveMortalityMatrix(species, matrixData)

        if (statusVersionRef.current !== statusVersion) {
          return
        }

        setStatus(result.syncState)

        if (!result.syncPromise) {
          return
        }

        void result.syncPromise.then((nextStatus) => {
          if (statusVersionRef.current !== statusVersion) {
            return
          }
          setStatus(nextStatus)
        })
      } catch (error) {
        console.error('Failed to save mortality matrix', error)

        if (statusVersionRef.current !== statusVersion) {
          return
        }

        setStatus('error')
        setErrorMessage(t('mortalityManagement.status.saveFailed'))
      }
    }, SAVE_DEBOUNCE_MS)

    return () => {
      if (saveTimeoutRef.current) {
        globalThis.clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [matrixData, persistenceMode, species, t])

  useEffect(() => {
    if (persistenceMode === 'external') {
      return
    }

    if (status !== 'offline' && status !== 'pending') {
      return
    }

    let cancelled = false

    const retrySync = async () => {
      const nextSyncState = await retryMortalityMatrixSync(species)

      if (cancelled) {
        return
      }

      if (nextSyncState !== 'synced') {
        setStatus(nextSyncState)
        return
      }

      statusVersionRef.current += 1
      setStatus('refreshing')
      setErrorMessage(null)

      const result = await loadMortalityMatrix(species, { forceRefresh: true })

      if (cancelled) {
        return
      }

      skipNextSaveRef.current = true
      const cloned = cloneMatrix(result.matrix)
      setMatrixData(cloned)
      onMatrixChangeRef.current?.(cloned)
      setDataSource(result.source)

      if (result.errorMessage) {
        setStatus('error')
        setErrorMessage(
          isBlankMatrix(result.matrix)
            ? t('mortalityManagement.status.refreshFailedBlank')
            : t('mortalityManagement.status.refreshFailed')
        )
        return
      }

      setStatus(result.syncState)
    }

    const handleOnline = () => {
      void retrySync()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      void retrySync()
    }

    const handleFocus = () => {
      void retrySync()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    void retrySync()

    return () => {
      cancelled = true
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [persistenceMode, species, status, t])

  const statusPresentation = useMemo(
    () => persistenceMode === 'external'
      ? null
      : getMortalityMatrixStatusPresentation(status, (key) => t(key as any), errorMessage),
    [errorMessage, persistenceMode, status, t]
  )

  useEffect(() => {
    if (persistenceMode === 'external') {
      return
    }

    onStatusChange?.(statusPresentation)
  }, [onStatusChange, persistenceMode, statusPresentation])

  const handleDataChange = (nextData: MatrixData) => {
    skipNextSaveRef.current = false
    const cloned = cloneMatrix(nextData)
    if (persistenceMode === 'species') {
      cacheMatrix(species, cloned)
      markLocalMortalityMatrixEdit(species)
      setDataSource('local')
    }
    setMatrixData(cloned)
    onMatrixChangeRef.current?.(cloned)
  }

  const isBusy = status === 'loading' || status === 'refreshing'
  const blank = persistenceMode === 'species' && isBlankMatrix(matrixData)
  // While the very first load is in flight there is no prior data to show, so a
  // grid of placeholder zeros would read as "0% mortality everywhere". Show a
  // skeleton until the load settles (demo finding 4.4).
  const showSkeleton = persistenceMode === 'species' && status === 'loading'
  // On a hard load failure with nothing cached, the zero grid is misleading —
  // the error notice + Retry stand alone instead.
  const hideTableForError = persistenceMode === 'species' && status === 'error' && blank
  const showTable = !showSkeleton && !hideTableForError
  const showBlankNotice =
    persistenceMode === 'species' && !showSkeleton && status !== 'error' && status !== 'saving' && !isBusy && blank

  return (
    <div>
      {!onStatusChange && statusPresentation && (
        <div aria-live="polite" role="status" style={statusBannerStyle(statusPresentation.tone)}>
          {statusPresentation.message}
        </div>
      )}

      {persistenceMode === 'species' && status === 'error' && (
        <div style={noticeCardStyle('error')}>
          <button
            type="button"
            onClick={() => setRetryToken((value) => value + 1)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #b91c1c',
              background: '#fff',
              color: '#b91c1c',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {t('mortalityManagement.action.retry')}
          </button>
        </div>
      )}

      {showBlankNotice && (
        <div role="note" style={noticeCardStyle('info')}>
          <strong style={{ display: 'block', marginBottom: 4 }}>
            {t('mortalityManagement.empty.title')}
          </strong>
          {dataSource === 'server'
            ? t('mortalityManagement.empty.bodyServer')
            : t('mortalityManagement.empty.body')}
        </div>
      )}

      {showSkeleton && <MatrixSkeleton label={t('mortalityManagement.status.loading')} />}

      {showTable && (
        <MatrixTable
          title={t('mortalityManagement.matrixTitle')}
          data={matrixData}
          onDataChange={handleDataChange}
          rowLabel={t('mortalityManagement.rowLabel')}
          columnLabel={t('mortalityManagement.columnLabel')}
          valueLabel={t('mortalityManagement.valueLabel')}
          editable={true}
          precision={4}
        />
      )}
    </div>
  )
}

export default MortalityMatrix
